import BigNumber from 'bignumber.js';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { fetchBaseCurrencyChange, useFeesBySolutionContext } from 'sonata-platform-components';
import { LoadingSkeleton, Range, Switch, useQueriesFetch } from 'sonata-react-components';
import { BaseCurrencyEnum } from 'sonata-static-storage/lib/enum/baseCurrency.enum';

type FetchedCurrencies = {
  change: number;
  currency: string;
};

function AttributionModelValues() {
  const { formatMessage } = useIntl();
  const currencyTokens: Record<string, any> = {};
  const { currencyInfo, getStep4, handleStep4Change } = useFeesBySolutionContext();

  const { attributionModel, attributionModelConfigValues, attributionModelValue } = getStep4();

  const defaultAttributionModelValue = attributionModelConfigValues.defaultValues;

  const handleAttributionModelValuesChange = (newValues: number | number[]) => {
    handleStep4Change({
      attributionModelValue: {
        max: new BigNumber((newValues as number[])[1]).decimalPlaces(2).toNumber(),
        min: new BigNumber((newValues as number[])[0]).decimalPlaces(2).toNumber()
      }
    });
  };

  useEffect(() => {
    handleStep4Change({
      attributionModelValue: attributionModelValue || defaultAttributionModelValue
    });
    return () => {
      Object.keys(currencyTokens).forEach(currencyToken => {
        currencyTokens[currencyToken]?.cancel?.();
      });
    };
  }, []);

  const currenciesToFetch = [...new Set([BaseCurrencyEnum.EUR, currencyInfo.baseCurrency])];
  const { data: currencies, statuses: currenciesFetchStatuses } = useQueriesFetch<FetchedCurrencies[]>(
    currenciesToFetch.map((currency: keyof typeof BaseCurrencyEnum) => {
      currencyTokens[currency] = {};
      return {
        enabled: !!currencyInfo.baseCurrency && currencyInfo.baseCurrency !== 'EUR',
        queryFn: async () => {
          const currencyChange = await fetchBaseCurrencyChange(currency, currencyTokens[currency]);
          return {
            change: currencyChange,
            currency
          };
        },
        queryKey: ['currenciesChanges', currency]
      };
    })
  );

  const currenciesAreReady =
    currenciesToFetch.length === 1 || currenciesFetchStatuses.every((status: string) => status === 'success');
  if (currenciesAreReady && currencyInfo.baseCurrency !== 'EUR') {
    defaultAttributionModelValue.max = new BigNumber(defaultAttributionModelValue.max)
      .dividedBy((currencies as FetchedCurrencies[]).find(({ currency }) => currency === 'EUR')!.change)
      .multipliedBy(
        (currencies as FetchedCurrencies[]).find(({ currency }) => currency === currencyInfo.baseCurrency)!.change
      )
      .decimalPlaces(2)
      .toNumber();
    defaultAttributionModelValue.min = new BigNumber(defaultAttributionModelValue.min)
      .dividedBy((currencies as FetchedCurrencies[]).find(({ currency }) => currency === 'EUR')!.change)
      .multipliedBy(
        (currencies as FetchedCurrencies[]).find(({ currency }) => currency === currencyInfo.baseCurrency)!.change
      )
      .decimalPlaces(2)
      .toNumber();
  }
  const showLoader = attributionModel === 'mediafee_visitcost' && !currenciesAreReady;

  return (
    <Switch>
      <Switch.Case condition={showLoader}>
        <div className="attributionModelRangeLoader">
          <LoadingSkeleton count={1} height="100%" width="100%" />
        </div>
      </Switch.Case>
      <Switch.Default>
        <Range
          className="attributionModelRange"
          id="campaignDetails_attributionModelValueSlider"
          max={100}
          min={0}
          onChange={handleAttributionModelValuesChange}
          type="range"
          value={[
            new BigNumber(attributionModelValue?.min || defaultAttributionModelValue.min).decimalPlaces(2).toNumber(),
            new BigNumber(attributionModelValue?.max || defaultAttributionModelValue.max).decimalPlaces(2).toNumber()
          ]}
        >
          <Range.Label className="attributionModelRangeLabel" required>
            {formatMessage(
              {
                defaultMessage:
                  attributionModel === 'imp_visitrate' ? 'Expected Visit Rate (%)' : 'Expected Visit Cost ({currency})',
                id: `feesBySolution.stepFour.attributionModel.slider.${
                  attributionModel === 'imp_visitrate' ? 'expectedVisitRate' : 'expectedVisitCost'
                }`
              },
              {
                currency: currencyInfo.currencySymbol
              }
            )}
          </Range.Label>
          <Range.Input className="attributionModelRangeInput" decimalDigits={2} />
        </Range>
      </Switch.Default>
    </Switch>
  );
}

export default AttributionModelValues;
